import { ResponsiveModal } from "@components/responsive-modal";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { ChevronDownIcon, Select, TextField } from "@radix-ui/themes";
import { motion } from "framer-motion";
import { isValidElement, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

export type SelectItem = {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
  group?: string;
};

interface ResponsiveSelectProps {
  value: string;
  onChange: (value: string) => void;
  items: SelectItem[];
  placeholder?: string;
  searchPlaceholder?: string;
  renderTrigger?: (value?: SelectItem) => React.ReactNode;
  size?: "1" | "2" | "3";
  className?: string;
  variant?: "default" | "invisible";
  disabled?: boolean;
}

export const ResponsiveSelect = ({
  value,
  onChange,
  items,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  renderTrigger,
  size = "2",
  className = "",
  disabled,
}: ResponsiveSelectProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Find the currently selected item
  const selectedItem = items.find((item) => item.value === value);

  // Check if device is mobile on mount and window resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Filter items based on search query
  const filteredItems = items.filter((item) => {
    if (!searchQuery) return true;

    // Extract text from label if it's a ReactNode
    let labelText = "";
    if (typeof item.label === "string") {
      labelText = item.label;
    } else if (item.label && isValidElement(item.label)) {
      // Try to extract text content from React elements
      const extractText = (node: React.ReactNode): string => {
        if (typeof node === "string") return node;
        if (typeof node === "number") return node.toString();
        if (!node || typeof node !== "object") return "";

        if (isValidElement(node)) {
          const children = node.props.children;
          if (Array.isArray(children)) {
            return children.map(extractText).join(" ");
          }
          return extractText(children);
        }

        return "";
      };

      labelText = extractText(item.label);
    }

    return labelText.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Group items by their group property
  const groupedItems = filteredItems.reduce((acc, item) => {
    const group = item.group || "";
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(item);
    return acc;
  }, {} as Record<string, SelectItem[]>);

  // For mobile view - render a bottom sheet
  if (isMobile) {
    return (
      <>
        <motion.div
          className={twMerge(
            "select-container cursor-pointer border border-slate-300 dark:border-slate-700 rounded-full p-2 h-10 box-border pr-4",
            "flex items-center justify-between",
            size === "1" && "text-xs h-6 pr-1",
            size === "2" && "text-sm h-8 pr-3",
            disabled && "opacity-50 pointer-events-none",
            className
          )}
          onClick={() => setIsOpen(true)}
        >
          <div className="grow w-min-0 overflow-hidden text-ellipsis">
            {renderTrigger ? (
              renderTrigger(selectedItem)
            ) : (
              <div>{selectedItem?.label || placeholder}</div>
            )}
          </div>

          <ChevronDownIcon className="shrink-0 ml-1 w-2.5 h-2.5" />
        </motion.div>

        <ResponsiveModal
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            setSearchQuery("");
          }}
          title={placeholder}
          height="auto"
          maxHeight="80vh"
          header={
            items?.length > 10 ? (
              <TextField.Root
                size={size}
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              >
                <TextField.Slot>
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </TextField.Slot>
              </TextField.Root>
            ) : undefined
          }
        >
          <div className="flex flex-col gap-2 pb-4">
            {Object.entries(groupedItems).map(([group, groupItems]) => (
              <div key={group || "default"} className="mb-2">
                {group && (
                  <div className="text-sm font-semibold text-gray-500 mb-1 px-1">
                    {group}
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  {groupItems.map((item) => (
                    <div
                      key={item.value}
                      className={`p-2 rounded-lg ${
                        item.disabled
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      } ${
                        value === item.value
                          ? "bg-gray-100 dark:bg-gray-700"
                          : ""
                      }`}
                      onClick={() => {
                        if (!item.disabled) {
                          onChange(item.value);
                          setIsOpen(false);
                          setSearchQuery("");
                        }
                      }}
                    >
                      {item.label}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {filteredItems.length === 0 && (
              <div className="text-center text-gray-500 py-4">
                No results found
              </div>
            )}
          </div>
        </ResponsiveModal>
      </>
    );
  }

  // For desktop view - render Radix Select
  return (
    <Select.Root
      value={value}
      onValueChange={onChange}
      size={size}
      disabled={disabled}
    >
      <Select.Trigger className={className}>
        {renderTrigger ? (
          renderTrigger(selectedItem)
        ) : (
          <>{selectedItem?.label || placeholder}</>
        )}
      </Select.Trigger>
      <Select.Content position="popper">
        {Object.entries(groupedItems).map(([group, groupItems]) => (
          <Select.Group key={group || "default"}>
            {group && <Select.Label>{group}</Select.Label>}
            {groupItems.map((item) => (
              <Select.Item
                key={item.value}
                value={item.value}
                disabled={item.disabled}
              >
                <span>{item.label}</span>
              </Select.Item>
            ))}
          </Select.Group>
        ))}
        {filteredItems.length === 0 && (
          <div className="text-center text-gray-500 py-2">No results found</div>
        )}
      </Select.Content>
    </Select.Root>
  );
};
